import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Wordmark } from "@/components/site/Logo";
import { scrollToId } from "@/lib/motion";
import { track } from "@/lib/analytics";

const NAV = [
  { name: "Home", id: "hero", testId: "nav-item-home" },
  { name: "Services", id: "services", testId: "nav-item-services" },
  { name: "Our Work", id: "projects", testId: "nav-item-projects" },
  { name: "About", id: "about", testId: "nav-item-about" },
  { name: "Contact", id: "quote", testId: "nav-item-contact" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => { setOpen(false); scrollToId(id); };
  const light = !scrolled;

  return (
    <header
      data-testid="nav-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-white/85 backdrop-blur-xl border-b border-slate-200 shadow-sm" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[70px] flex items-center justify-between">
        <button data-testid="nav-logo-link" onClick={() => go("hero")} className="shrink-0">
          <Wordmark light={light} iconSize={32} />
        </button>

        <nav className="hidden md:flex items-center gap-1">
          {NAV.map((n) => (
            <button
              key={n.id}
              data-testid={n.testId}
              onClick={() => go(n.id)}
              className={`px-4 py-2 text-sm font-medium rounded-full transition-colors ${
                light ? "text-slate-200 hover:text-white hover:bg-white/10" : "text-slate-600 hover:text-navy hover:bg-slate-100"
              }`}
            >
              {n.name}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="nav-quote-cta"
            onClick={() => { track("cta_click", { section: "nav" }); go("quote"); }}
            className="hidden sm:inline-flex items-center rounded-full bg-brand-blue px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-brand-blue/25 hover:bg-blue-500 transition-colors"
          >
            Request a Quote
          </button>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className={`md:hidden p-2 rounded-lg ${light ? "text-white" : "text-navy"}`}
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-white border-b border-slate-200"
          >
            <div className="px-5 py-4 flex flex-col gap-1">
              {NAV.map((n) => (
                <button key={n.id} onClick={() => go(n.id)}
                  className="text-left px-4 py-3 rounded-lg text-slate-700 font-medium hover:bg-slate-100">
                  {n.name}
                </button>
              ))}
              <button onClick={() => go("quote")}
                className="mt-2 rounded-full bg-brand-blue px-5 py-3 text-sm font-semibold text-white">
                Request a Quote
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
